import { Box } from "@mui/material";
import { Alert, Checkbox, Form, message, Modal, Spin, Switch, Typography } from "antd";
import { useEffect, useRef, useState } from "react";
import axiosInstance from "../../../axios/axios-instance";
import {
  PermissionGroup,
  RoleDetail,
  RoleScopeType,
  RoleSummary,
  SCOPE_LABELS,
} from "../../../@type/role.types";
import ActionButton from "../../../components/common/buttons/ActionButton";
import CancelButton from "../../../components/common/buttons/CancelButton";
import InputField from "../../../components/common/fields/InputField";
import SelectField from "../../../components/common/fields/SelectField";
import { pathAPI } from "../../../utils/pathApi";

interface Props {
  open: boolean;
  roleId: number | null;
  allRoles: RoleSummary[];
  readOnly: boolean;
  onClose: () => void;
  onSaved: () => void;
}

interface FormValues {
  code?: string;
  name: string;
  description?: string;
  scope_type: RoleScopeType;
  multi_country: boolean;
  active: boolean;
  assignable_role_ids: number[];
}

const errorMessage = (e: unknown): string => {
  const err = e as { message?: string; response?: { data?: { message?: string } } };
  return err?.response?.data?.message || err?.message || "Request failed";
};

const RoleFormModal = ({ open, roleId, allRoles, readOnly, onClose, onSaved }: Props) => {
  const [form] = Form.useForm<FormValues>();
  const [groups, setGroups] = useState<PermissionGroup[]>([]);
  const [selected, setSelected] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isSystem, setIsSystem] = useState(false);
  const isEdit = roleId !== null;
  const scopeType = Form.useWatch("scope_type", form);
  // Bumps per open so a late response from a previous item is ignored.
  const requestSeqRef = useRef(0);

  useEffect(() => {
    if (!open) return;
    const seq = ++requestSeqRef.current;
    setLoading(true);
    form.resetFields();
    const loadGroups = axiosInstance
      .get(pathAPI.permissions)
      .then((r) => {
        if (seq !== requestSeqRef.current) return;
        setGroups(r.data?.data ?? []);
      });
    const loadRole = isEdit
      ? axiosInstance.get(`${pathAPI.roles}/${roleId}`).then((r) => {
          if (seq !== requestSeqRef.current) return;
          const role: RoleDetail = r.data.data;
          setIsSystem(role.is_system);
          setSelected(role.permission_ids);
          form.setFieldsValue({
            code: role.code,
            name: role.name,
            description: role.description ?? "",
            scope_type: role.scope_type,
            multi_country: role.multi_country,
            active: role.active,
            assignable_role_ids: role.assignable_role_ids,
          });
        })
      : Promise.resolve().then(() => {
          if (seq !== requestSeqRef.current) return;
          setIsSystem(false);
          setSelected([]);
          form.setFieldsValue({
            scope_type: "COUNTRY",
            multi_country: true,
            active: true,
            assignable_role_ids: [],
          });
        });
    Promise.all([loadGroups, loadRole])
      .catch((e) => {
        if (seq !== requestSeqRef.current) return;
        message.error(errorMessage(e));
      })
      .finally(() => {
        if (seq === requestSeqRef.current) setLoading(false);
      });
  }, [open, roleId]);

  const toggleGroup = (group: PermissionGroup, checked: boolean) => {
    const ids = group.permissions.map((p) => p.id);
    setSelected((prev) => (checked ? Array.from(new Set([...prev, ...ids])) : prev.filter((id) => !ids.includes(id))));
  };

  const onSubmit = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      let id = roleId;
      if (isEdit) {
        await axiosInstance.put(`${pathAPI.roles}/${roleId}`, {
          name: values.name,
          description: values.description,
          multi_country: values.multi_country,
          active: values.active,
        });
        await axiosInstance.put(`${pathAPI.roles}/${roleId}/permissions`, { permission_ids: selected });
      } else {
        const res = await axiosInstance.post(pathAPI.roles, {
          code: values.code,
          name: values.name,
          description: values.description,
          scope_type: values.scope_type,
          multi_country: values.scope_type === "COUNTRY" ? values.multi_country : false,
          active: values.active,
          permission_ids: selected,
        });
        id = res.data.data.id;
      }
      await axiosInstance.put(`${pathAPI.roles}/${id}/assignable`, {
        assignable_role_ids: values.assignable_role_ids ?? [],
      });
      message.success("Saved");
      onSaved();
    } catch (e) {
      message.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const assignableOptions = allRoles
    .filter((r) => r.id !== roleId && r.code !== "SUPER_ADMIN")
    .map((r) => ({ value: r.id, label: `${r.name} (${r.code})` }));
  const locked = readOnly || (isEdit && isSystem && form.getFieldValue("code") === "SUPER_ADMIN");

  return (
    <Modal
      open={open}
      width={900}
      centered
      title={isEdit ? "Edit role" : "Create role"}
      onCancel={onClose}
      footer={
        <Box display="flex" justifyContent="flex-end" gap={1.5}>
          <CancelButton onClick={onClose}>Cancel</CancelButton>
          <ActionButton onClick={onSubmit} loading={saving} disabled={locked}>
            Save
          </ActionButton>
        </Box>
      }
    >
      <Spin spinning={loading}>
        {locked && <Alert type="info" showIcon className="mb-3" message="This role cannot be modified." />}
        <Form form={form} layout="vertical" disabled={locked}>
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item
              name="name"
              label="Role name"
              rules={[{ required: true, message: "Please enter role name" }]}
            >
              <InputField disabled={locked} />
            </Form.Item>
            <Form.Item
              name="code"
              label="Role code"
              tooltip="Unique, upper case, cannot be changed later"
              rules={[
                { required: !isEdit, message: "Please enter role code" },
                { pattern: /^[A-Za-z][A-Za-z0-9_]*$/, message: "Letters, digits and underscore only" },
              ]}
            >
              <InputField disabled={isEdit || locked} placeholder="MARKETING" />
            </Form.Item>
            <Form.Item name="scope_type" label="Data scope" tooltip="Which data the role can see">
              <SelectField
                disabled={isEdit || locked}
                searchable={false}
                matchAnchorWidth
                options={(Object.keys(SCOPE_LABELS) as RoleScopeType[]).map((k) => ({
                  value: k,
                  label: SCOPE_LABELS[k],
                }))}
              />
            </Form.Item>
            <Form.Item name="assignable_role_ids" label="Can create / assign users with roles">
              <SelectField multiple disabled={locked} options={assignableOptions} placeholder="None" />
            </Form.Item>
          </div>
          <Form.Item name="description" label="Description">
            <InputField multiline minRows={2} disabled={locked} />
          </Form.Item>
          <div className="flex gap-8">
            {scopeType === "COUNTRY" && (
              <Form.Item name="multi_country" label="Allow multiple countries" valuePropName="checked">
                <Switch />
              </Form.Item>
            )}
            <Form.Item name="active" label="Active" valuePropName="checked">
              <Switch />
            </Form.Item>
          </div>
        </Form>

        <Typography.Title level={5}>Permissions ({selected.length})</Typography.Title>
        <div className="max-h-[360px] overflow-y-auto border rounded p-3">
          {groups.map((group) => {
            const ids = group.permissions.map((p) => p.id);
            const count = ids.filter((id) => selected.includes(id)).length;
            return (
              <div key={group.module} className="mb-3">
                <Checkbox
                  disabled={locked}
                  checked={count === ids.length}
                  indeterminate={count > 0 && count < ids.length}
                  onChange={(e) => toggleGroup(group, e.target.checked)}
                >
                  <b>{group.module}</b>
                </Checkbox>
                <div className="ml-6 flex flex-wrap gap-x-4">
                  {group.permissions.map((p) => (
                    <Checkbox
                      key={p.id}
                      disabled={locked}
                      checked={selected.includes(p.id)}
                      onChange={(e) =>
                        setSelected((prev) => (e.target.checked ? [...prev, p.id] : prev.filter((id) => id !== p.id)))
                      }
                    >
                      <span title={p.description ?? p.code}>{p.name || p.action}</span>
                    </Checkbox>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </Spin>
    </Modal>
  );
};

export default RoleFormModal;
